import { motion } from 'framer-motion'
import { ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { whyUs, seoMeta } from '../data/pages'
import { useScrollAnimation } from '../hooks/useScrollAnimation'
import { getIcon } from '../components/IconMap'
import SEO from '../components/SEO'

export default function WhyUsPage() {
  const { ref: diffRef, isVisible: diffVisible } = useScrollAnimation()
  const { ref: processRef, isVisible: processVisible } = useScrollAnimation()

  return (
    <>
      <SEO title={seoMeta.whyUs.title} description={seoMeta.whyUs.description} />

      <section className="relative bg-earth-900 pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blossom-950 via-earth-900 to-prime-950" />
        <div className="container-wide section-padding relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="badge-green mb-4">Why Choose Us</span>
            <h1 className="text-display-1 font-bold text-white mb-4">The Blossom Prime Difference</h1>
            <p className="text-earth-300 text-lg max-w-2xl">We combine trained operatives, professional-grade equipment and robust quality control to deliver results our clients can rely on.</p>
          </motion.div>
        </div>
      </section>

      <section className="section-padding-y section-padding bg-earth-50" ref={diffRef}>
        <div className="container-wide">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyUs.differentiators.map((item, index) => {
              const Icon = getIcon(item.icon)
              return (
                <motion.div key={item.title} initial={{ opacity: 0, y: 30 }} animate={diffVisible ? { opacity: 1, y: 0 } : {}} transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-2xl p-8 shadow-sm border border-earth-100 hover:shadow-elevated transition-all duration-500">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blossom-100 to-prime-100 flex items-center justify-center mb-5">
                    <Icon className="w-7 h-7 text-blossom-600" />
                  </div>
                  <h3 className="text-xl font-bold text-earth-900 mb-3">{item.title}</h3>
                  <p className="text-earth-500 leading-relaxed text-sm">{item.description}</p>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      <section className="section-padding-y section-padding bg-earth-900" ref={processRef}>
        <div className="container-wide">
          <h2 className="text-display-3 font-bold text-white text-center mb-12">Our Process</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyUs.process.map((step, index) => {
              const Icon = getIcon(step.icon)
              return (
                <motion.div key={step.step} initial={{ opacity: 0, y: 30 }} animate={processVisible ? { opacity: 1, y: 0 } : {}} transition={{ delay: index * 0.15 }} className="relative">
                  <div className="text-6xl font-black text-white/10 mb-4">{step.step}</div>
                  <div className="w-12 h-12 rounded-xl bg-blossom-600 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{step.title}</h3>
                  <p className="text-earth-400 text-sm leading-relaxed">{step.description}</p>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      <section className="section-padding-y section-padding bg-white">
        <div className="container-wide">
          <h2 className="text-display-3 font-bold text-earth-900 text-center mb-12">Certifications & Compliance</h2>
          <div className="flex flex-wrap justify-center gap-4">
            {whyUs.certifications.map((cert) => (
              <div key={cert.name} className="px-6 py-4 bg-earth-50 rounded-xl border border-earth-100 text-center">
                <div className="font-bold text-earth-900">{cert.name}</div>
                <div className="text-sm text-earth-500">{cert.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding-y section-padding bg-gradient-to-r from-blossom-800 to-blossom-700">
        <div className="container-wide text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Work Together?</h2>
          <p className="text-blossom-100 mb-8 max-w-xl mx-auto">Get a clear, no-obligation quotation within 24 hours. Our team is ready to discuss your requirements and put the right solution in place.</p>
          <Link to="/contact" className="btn-primary bg-white text-blossom-700 hover:bg-earth-50">Get Your Free Quote<ArrowRight className="w-5 h-5" /></Link>
        </div>
      </section>
    </>
  )
}
